import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

const navItems: NavItem[] = [
  { path: "/", label: "Dashboard", icon: "ri-dashboard-line" },
  { path: "/musteriler", label: "Müşteri Rehberi", icon: "ri-contacts-line" },
  { path: "/alacaklar", label: "Müşteri Alacakları", icon: "ri-hand-coin-line" },
  { path: "/borclar", label: "Borç Takibi", icon: "ri-wallet-3-line" },
  { path: "/siparisler", label: "Siparişler", icon: "ri-shopping-bag-3-line" },
  { path: "/uyarilar", label: "Akıllı Uyarılar", icon: "ri-notification-3-line" },
  { path: "/raporlar", label: "Raporlar", icon: "ri-bar-chart-2-line" },
  { path: "/mesaj-sablonlari", label: "Mesaj Şablonları", icon: "ri-message-3-line" },
];

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef<HTMLElement>(null);

  // Close on escape key
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape" && mobileOpen) {
        onMobileClose();
      }
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [mobileOpen, onMobileClose]);

  const handleNavigate = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-screen bg-dark-950 border-r border-white/5 z-40 flex-col w-64">
        {/* Logo Area */}
        <div className="h-16 flex items-center px-4 border-b border-white/5 shrink-0">
          <div className="flex items-center gap-2.5">
            <img
              src="https://public.readdy.ai/ai/img_res/50a43909-6850-4301-aff7-94fe8329b2b3.png"
              alt="KuyumPro"
              className="w-8 h-8 object-contain"
            />
            <span className="font-playfair text-gold-400 text-lg font-semibold tracking-wide">
              KuyumPro
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                className={`sidebar-link w-full text-left cursor-pointer ${isActive ? "active" : ""}`}
              >
                <i className={`${item.icon} text-lg ${isActive ? "text-gold-400" : ""}`} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="p-3 border-t border-white/5 shrink-0">
          <button className="sidebar-link w-full text-left cursor-pointer">
            <i className="ri-settings-4-line text-lg" />
            <span>Ayarlar</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay Backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside
        ref={sidebarRef}
        className={`lg:hidden fixed top-0 bottom-0 left-0 bg-dark-950 border-r border-white/5 z-50 flex flex-col w-64 transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo + Close */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/5 shrink-0">
          <div className="flex items-center gap-2.5">
            <img
              src="https://public.readdy.ai/ai/img_res/50a43909-6850-4301-aff7-94fe8329b2b3.png"
              alt="KuyumPro"
              className="w-8 h-8 object-contain"
            />
            <span className="font-playfair text-gold-400 text-lg font-semibold tracking-wide">
              KuyumPro
            </span>
          </div>
          <button
            onClick={onMobileClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-200 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                className={`sidebar-link w-full text-left cursor-pointer ${isActive ? "active" : ""}`}
              >
                <i className={`${item.icon} text-lg ${isActive ? "text-gold-400" : ""}`} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="p-3 border-t border-white/5 shrink-0">
          <button className="sidebar-link w-full text-left cursor-pointer">
            <i className="ri-settings-4-line text-lg" />
            <span>Ayarlar</span>
          </button>
        </div>
      </aside>
    </>
  );
}